import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { originService } from '../../../services/originService';
import { Input } from '../../ui/Input';

// Optional, searchable origin selector with "add my own" support.
export const OriginPicker = ({ value, note, onChange, onNoteChange }) => {
  const { user } = useAuth();
  const [origins, setOrigins] = useState([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    originService.list()
      .then((data) => { if (active) setOrigins(data); })
      .catch(() => { if (active) setError('Origins could not be loaded.'); });
    return () => { active = false; };
  }, []);

  const selected = origins.find((o) => o.id === value);
  const q = query.trim().toLowerCase();
  const matches = origins.filter((o) => o.label.toLowerCase().includes(q));
  const exact = origins.find((o) => o.label.toLowerCase() === q);

  const choose = (id) => {
    onChange(id);
    setQuery('');
    setError('');
  };

  const addOwn = async () => {
    setBusy(true);
    setError('');
    try {
      const created = await originService.create(user.id, query);
      setOrigins((list) => [...list, created]);
      choose(created.id);
    } catch (err) {
      setError(err.message || 'Could not save this origin.');
    } finally {
      setBusy(false);
    }
  };

  const removeOwn = async (origin) => {
    if (!window.confirm(`Delete "${origin.label}"? Moments using it will keep everything else.`)) return;
    try {
      await originService.remove(origin.id);
      setOrigins((list) => list.filter((o) => o.id !== origin.id));
      if (value === origin.id) onChange(null);
    } catch (err) {
      setError(err.message || 'Could not delete this origin.');
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-slate-700">How did this begin? <span className="text-slate-400">(optional)</span></p>
      {selected ? (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-sm font-semibold text-slate-800 break-words">{selected.label}</span>
          <button type="button" onClick={() => choose(null)} className="p-1 rounded-lg text-slate-400 hover:bg-slate-200" aria-label="Remove origin">
            <SafeIcon icon={FiIcons.FiX} />
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            maxLength={80}
            placeholder="Search origins, or type your own"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
          <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-100 divide-y divide-slate-100">
            {matches.map((o) => (
              <div key={o.id} className="flex items-center justify-between hover:bg-slate-50">
                <button type="button" onClick={() => choose(o.id)} className="flex-1 text-left px-3 py-2 text-sm text-slate-700">
                  {o.label}{o.owner_id ? <span className="ml-2 text-xs text-slate-400">Mine</span> : null}
                </button>
                {o.owner_id && (
                  <button type="button" onClick={() => removeOwn(o)} className="p-2 text-slate-300 hover:text-red-500" aria-label={`Delete ${o.label}`}>
                    <SafeIcon icon={FiIcons.FiTrash2} />
                  </button>
                )}
              </div>
            ))}
            {q && !exact && (
              <button type="button" onClick={addOwn} disabled={busy} className="block w-full text-left px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50">
                {busy ? 'Saving…' : `Add "${query.trim()}" as my own`}
              </button>
            )}
            {!matches.length && !q && <p className="px-3 py-2 text-sm text-slate-400">No origins available.</p>}
          </div>
        </>
      )}
      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
      {selected && (
        <Input
          placeholder="Details: event, venue, who introduced you…"
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          maxLength={300}
        />
      )}
    </div>
  );
};